import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { newsApi, resolveImageUrl } from '../lib/api';
import { getCrownVicImage, handleImageError } from '../lib/images';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Clock, Search, Tag, ThumbsUp, ChevronLeft, ChevronRight } from 'lucide-react';

const PAGE_SIZE = 12;

export default function NewsPage() {
  const [articles, setArticles] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [list, count] = await Promise.all([
          newsApi.getAll(page * PAGE_SIZE, PAGE_SIZE),
          newsApi.count(),
        ]);
        if (cancelled) return;
        setArticles(list.data);
        setTotal(count.data?.total || 0);
      } catch (e) {
        console.error('Error fetching news:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [page]);

  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  const filtered = articles.filter((a) =>
    a.title.toLowerCase().includes(searchTerm.toLowerCase())
    || a.summary?.toLowerCase().includes(searchTerm.toLowerCase())
    || a.tags?.some((tag) => tag.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const showPager = total > PAGE_SIZE && !searchTerm;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <div className="bg-[#141414] border-b border-[#262626]">
        <div className="container mx-auto px-4 py-12">
          <p className="tag text-[#FF3B30] mb-2">Crown Vic Racing</p>
          <h1 className="font-heading font-bold text-4xl md:text-5xl text-white mb-4">News & Updates</h1>
          <p className="text-gray-400 max-w-2xl">
            Stay up to date with the latest Crown Victoria short track racing news from tracks across the United States.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
          <Input
            type="text"
            placeholder="Search news..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-[#141414] border-[#262626] text-white focus:border-[#FF3B30]"
            data-testid="news-search-input"
          />
        </div>
      </div>

      <div className="container mx-auto px-4 pb-16">
        {loading ? (
          <div className="text-center py-16" data-testid="news-loading">
            <div className="w-12 h-12 border-4 border-[#FF3B30] border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="mt-4 text-gray-400 tag">Loading news...</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((article) => (
                <Link key={article.id} to={`/news/${article.id}`} data-testid={`news-article-${article.id}`}>
                  <Card className="bg-[#141414] border-[#262626] overflow-hidden card-hover h-full flex flex-col">
                    <div className="relative h-48 bg-[#1F1F1F]">
                      <img
                        src={resolveImageUrl(article.image_url) || getCrownVicImage(article.id)}
                        alt={article.title}
                        className="w-full h-full object-cover"
                        onError={(e) => handleImageError(e, article.id)}
                      />
                    </div>
                    <div className="p-5 flex-1 flex flex-col">
                      <div className="flex flex-wrap gap-2 mb-3">
                        {article.tags?.slice(0, 3).map((tag, i) => (
                          <span key={i} className="tag bg-[#262626] text-gray-300 px-2 py-1 rounded-sm flex items-center">
                            <Tag className="w-2.5 h-2.5 mr-1" />
                            {tag}
                          </span>
                        ))}
                      </div>
                      <h2 className="font-heading font-bold text-lg text-white mb-2 line-clamp-2">{article.title}</h2>
                      <p className="text-gray-400 text-sm line-clamp-3 mb-4 flex-1">{article.summary}</p>
                      <div className="flex items-center justify-between text-xs text-gray-500 mt-auto pt-4 border-t border-[#262626]">
                        <span className="flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          {formatDate(article.created_at)}
                        </span>
                        {article.source_name && <span>{article.source_name}</span>}
                        {(article.likes > 0) && (
                          <span className="flex items-center gap-1 text-green-400">
                            <ThumbsUp className="w-3 h-3" />
                            {article.likes}
                          </span>
                        )}
                      </div>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>

            {filtered.length === 0 && (
              <div className="text-center py-16" data-testid="news-empty">
                <p className="text-gray-500 text-lg">No articles found.</p>
              </div>
            )}

            {showPager && (
              <div className="flex items-center justify-between mt-10 pt-6 border-t border-[#262626]" data-testid="news-pager">
                <p className="text-sm text-gray-500">
                  Page {page + 1} of {totalPages} · {total} articles
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="border-[#262626] text-white hover:bg-[#1F1F1F]"
                    data-testid="news-prev-btn"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" /> Previous
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                    className="border-[#262626] text-white hover:bg-[#1F1F1F]"
                    data-testid="news-next-btn"
                  >
                    Next <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
